import type React from 'react'
import { useCallback, useMemo, useState } from 'react'
import { PageContext, type PageContextType, type TabCounts } from './context'
import type { BackButtonConfig, PageAction, PageTabsConfig } from './types'

// Re-exported for compatibility only. Import the hook from './context' directly — see the note there on
// why reaching it through this module (which exports a component) desyncs the context under Fast Refresh.
export { usePageContext } from './context'

// context provider component, which wraps the application
export function PageProvider({ children }: { children: React.ReactNode }) {
	const [actions, setActions] = useState<PageAction[]>([])
	const [tabCounts, setTabCounts] = useState<TabCounts>({})
	const [dynamicTabs, setDynamicTabs] = useState<PageTabsConfig | null>(null)
	const [backButton, setBackButtonState] = useState<BackButtonConfig | null>(null)
	const [hideHeader, setHideHeader] = useState<boolean>(false)
	const [hideCustomViews, setHideCustomViews] = useState<boolean>(false)
	// Set straight from a ref callback in PageHeader; useState setters are stable, so the ref never churns.
	const [headerControlsSlot, setHeaderControlsSlot] = useState<HTMLElement | null>(null)

	const setPageActions = useCallback((newActions: PageAction[]) => {
		setActions(newActions)
	}, [])

	// updates action properties like loading, disabled, etc.
	const updatePageAction = useCallback((actionId: string, updates: Partial<PageAction>) => {
		setActions(prev =>
			prev.map(action => {
				if (action.id !== actionId) {
					return action
				}
				// Type assertion is safe here because we're only updating properties
				// that exist on the original action type
				return { ...action, ...updates } as PageAction
			}),
		)
	}, [])

	// Clear all actions - useful for cleanup on page unmount
	const clearPageActions = useCallback(() => {
		setActions([])
	}, [])

	const setPageTabs = useCallback((tabs: PageTabsConfig | null) => {
		setDynamicTabs(tabs)
	}, [])

	const clearPageTabs = useCallback(() => {
		setDynamicTabs(null)
	}, [])

	const setBackButton = useCallback((config: BackButtonConfig | null) => {
		setBackButtonState(config)
	}, [])

	const clearBackButton = useCallback(() => {
		setBackButtonState(null)
	}, [])

	// Every setter here is stable — a useState setter, or a `useCallback(…, [])` — so this only re-identifies
	// when a value actually changes. Without it the provider hands every consumer a new object on each of its
	// own renders, and the whole header re-renders for state it never reads.
	const contextValue: PageContextType = useMemo(
		() => ({
			actions,
			setPageActions,
			updatePageAction,
			clearPageActions,
			tabCounts,
			setTabCounts,
			dynamicTabs,
			setPageTabs,
			clearPageTabs,
			backButton,
			setBackButton,
			clearBackButton,
			hideHeader,
			setHideHeader,
			hideCustomViews,
			setHideCustomViews,
			headerControlsSlot,
			setHeaderControlsSlot,
		}),
		[
			actions,
			setPageActions,
			updatePageAction,
			clearPageActions,
			tabCounts,
			dynamicTabs,
			setPageTabs,
			clearPageTabs,
			backButton,
			setBackButton,
			clearBackButton,
			hideHeader,
			hideCustomViews,
			headerControlsSlot,
		],
	)

	return <PageContext.Provider value={contextValue}>{children}</PageContext.Provider>
}
